import {
  AvatarSizeVariant,
  Button,
  Modal,
  SkeletonLoader,
  UserAvatar,
  useToastContext,
} from '@payaca/ambrosia';
import {
  getAcceptedFileTypes,
  getMbFileLimit,
} from '@payaca/helpers/fileHelper';
import { requestPersistUploadAndLinkToEntity } from '@payaca/store/uploads/uploadsActions';
import {
  ImageEditConfig,
  UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP,
} from '@payaca/types/uploadTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { clsx } from '@payaca/utilities/cvaMerge';
import { FC, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper, { Area } from 'react-easy-crop';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useGetUser } from '../../../../api/queries/user/useGetUser';

const avatarOverlayClassName =
  'absolute inset-0 rounded-full flex justify-center items-center';

const imageFileTypes = getAcceptedFileTypes(['image']);

const UserPageHeaders: FC = () => {
  const { userId } = useParams<{ userId: string }>();

  const dispatch = useDispatch();

  const { data: userData, refetch: refetchUserData } = useGetUser(+userId);
  const { pushError } = useToastContext();

  const [image, setImage] = useState<string>();
  const [file, setFile] = useState<File>();
  const [isLoading, setIsLoading] = useState(false);

  const user = userData?.user;

  const onUploadFile = (editConfig: ImageEditConfig) => {
    if (!file) {
      return;
    }
    setIsLoading(true);

    dispatch(
      requestPersistUploadAndLinkToEntity(
        {
          file,
          fileName: file.name,
          entityId: +userId,
          entityType: 'user',
          entityRole: 'user-avatar',
          editConfig,
        },
        (error) => {
          if (error) {
            pushError('Failed to upload avatar');
          } else {
            void refetchUserData();
          }
        }
      )
    );

    setIsLoading(false);
  };
  const { getRootProps, getInputProps } = useDropzone({
    disabled: isLoading,
    maxFiles: 1,
    accept: imageFileTypes,
    onDrop: (files) => {
      const file = files[0];
      if (file.size > UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP) {
        pushError(
          `File size exceeds the ${getMbFileLimit(
            UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP
          )} limit.`
        );
        return;
      }
      setFile(files[0]);
      setImage(URL.createObjectURL(files[0]));
    },
  });

  const bannerColour = user?.colour ?? undefined;

  return (
    <div>
      <div
        className="-mx-4 -mt-4 h-36 rounded-t-xl md:-mx-5 md:-mt-5"
        style={{
          backgroundColor: bannerColour ? `${bannerColour}40` : undefined,
        }}
      />
      <div className="-mt-14 flex items-end gap-5 px-2">
        {!user ? (
          <>
            <SkeletonLoader.Circle className="h-28 w-28" />
            <SkeletonLoader.Title className="mb-6 w-48" />
          </>
        ) : (
          <>
            <div className="relative h-28 w-28 shrink-0">
              {isLoading ? (
                <SkeletonLoader.Circle className="h-28 w-28" />
              ) : (
                <>
                  <UserAvatar
                    sizeVariant={AvatarSizeVariant.XL}
                    user={{
                      firstName: user.firstName,
                      lastName: user.lastName,
                      emailAddress: user.email || '',
                      userColour: user.colour || undefined,
                      imgSrc: user.avatarUrl || undefined,
                    }}
                  />
                  {/* hover peer overlay */}
                  <div
                    className={clsx(
                      avatarOverlayClassName,
                      'peer z-10 cursor-pointer'
                    )}
                    {...getRootProps()}
                  />
                  <input {...getInputProps()} />

                  {/* transparent overlay */}
                  <div
                    className={clsx(
                      avatarOverlayClassName,
                      'bg-white opacity-0 transition-opacity peer-hover:opacity-40'
                    )}
                  />

                  {/* pencil overlay */}
                  <div
                    className={clsx(
                      avatarOverlayClassName,
                      'opacity-0 transition-all peer-hover:opacity-100'
                    )}
                  >
                    <UntitledIcon
                      name="edit-01.3"
                      className="h-6 w-6 text-blue-900"
                    />
                  </div>
                </>
              )}
            </div>
            <h1 className="mb-6">{user.fullName}</h1>
          </>
        )}
      </div>
      <ImageCropModal
        isOpen={!!image}
        onClose={() => setImage(undefined)}
        image={image}
        onUploadFile={onUploadFile}
      />
    </div>
  );
};

type ImageCropModalProps = {
  isOpen: boolean;
  onClose: () => void;
  image?: string;
  onUploadFile: (editConfig: ImageEditConfig) => void;
};
const ImageCropModal: FC<ImageCropModalProps> = (props) => {
  const { isOpen, onClose, image, onUploadFile } = props;
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();

  if (!image) {
    return null;
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change profile photo">
      <Modal.Body>
        <div className="relative h-72">
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onCropComplete={(_croppedArea: Area, croppedAreaPixels: Area) => {
              setCroppedAreaPixels(croppedAreaPixels);
            }}
            onZoomChange={setZoom}
            objectFit="cover"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            onClick={() => {
              if (croppedAreaPixels) {
                onUploadFile({
                  crop: croppedAreaPixels,
                  resize: { width: 200, height: 200 },
                });
                onClose();
              }
            }}
            disabled={!croppedAreaPixels}
          >
            Upload
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default UserPageHeaders;
